import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Copy, RotateCcw, Trash2, Plus, X, Smartphone, Code2, LayoutDashboard, TrendingUp } from 'lucide-react'
import { api, type ProjectWithStats } from '../lib/api'
import { Button, Input, Spinner, Empty, ConfirmModal } from '../components/ui'


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PROJECT_ICONS: any[] = [Smartphone, Code2, LayoutDashboard, TrendingUp]
const ICON_COLORS = [
  { bg: 'rgba(53,37,205,0.1)', color: '#3525cd' },
  { bg: 'rgba(126,48,0,0.1)', color: '#7e3000' },
  { bg: 'rgba(88,87,155,0.1)', color: '#58579b' },
  { bg: 'rgba(5,150,105,0.1)', color: '#059669' },
] as const

export function Projects() {
  const [projects, setProjects] = useState<ProjectWithStats[]>([])
  const [planLimit, setPlanLimit] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; confirmLabel: string; danger: boolean; onConfirm: () => void } | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    api.projects.list()
      .then(({ projects, plan_limit }) => {
        setProjects(projects)
        setPlanLimit(plan_limit)
      })
      .finally(() => setLoading(false))
  }, [])

  async function createProject() {
    if (!name.trim()) return
    setCreating(true)
    setCreateError(null)
    try {
      const project = await api.projects.create({ name: name.trim() })
      const withStats = { ...project, feedback_count: 0, open_count: 0, week_count: 0 }
      setProjects(prev => [withStats, ...prev])
      setShowModal(false)
      setName('')
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create project')
    } finally {
      setCreating(false)
    }
  }

  function rotateKey(id: string) {
    setConfirmModal({
      title: 'Rotate API Key',
      message: 'Your old key will stop working immediately. Any integrations using it will break.',
      confirmLabel: 'Rotate Key',
      danger: false,
      onConfirm: async () => {
        setConfirmModal(null)
        const { api_key } = await api.projects.rotateKey(id)
        setProjects(prev => prev.map(p => p.id === id ? { ...p, api_key } : p))
      },
    })
  }

  function deleteProject(id: string) {
    setConfirmModal({
      title: 'Delete Project',
      message: 'This will permanently delete the project and all its feedback. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null)
        await api.projects.delete(id)
        setProjects(prev => prev.filter(p => p.id !== id))
      },
    })
  }

  function copyKey(key: string) {
    navigator.clipboard.writeText(key)
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  function maskKey(key: string) {
    if (key.length <= 8) return key
    const prefix = key.slice(0, key.indexOf('_', key.indexOf('_') + 1) + 1)
    return prefix + '••••••••••••' + key.slice(-4)
  }

  if (loading) return (
    <div style={{ padding: '40px 48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner />
    </div>
  )

  return (
    <div style={{ padding: '40px 48px 48px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 36 }}>
        <div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0, letterSpacing: '-0.02em' }}>
            Active Workspaces
          </h2>
          <p style={{ fontSize: 14, color: '#777587', marginTop: 4, marginBottom: 0 }}>
            Manage feedback collection for all your digital products.
          </p>
          {planLimit !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <div style={{ height: 6, width: 140, borderRadius: 99, background: '#eceef0', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min((projects.length / planLimit) * 100, 100)}%`,
                  borderRadius: 99,
                  background: projects.length >= planLimit ? '#ba1a1a' : '#3525cd',
                  transition: 'width 0.3s',
                }} />
              </div>
              <span style={{ fontSize: 12, color: '#777587', fontWeight: 600 }}>
                {projects.length} / {planLimit === 999 ? '∞' : planLimit} projects used
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', background: '#3525cd', color: '#ffffff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              boxShadow: '0 4px 12px rgba(53,37,205,0.25)',
            }}
          >
            <Plus size={15} /> New Project
          </button>
        </div>
      </div>

      {projects.length === 0 ? (
        <Empty icon="📱" title="No projects yet"
          sub="Create your first project to get your API key"
          action={<Button onClick={() => setShowModal(true)}><Plus size={14} /> Create Project</Button>} />
      ) : (
        <>
          {/* Bento Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
            {projects.map((p, idx) => {
              const IconComp = PROJECT_ICONS[idx % PROJECT_ICONS.length]!
              const iconStyle = ICON_COLORS[idx % ICON_COLORS.length]!
              return (
                <div
                  key={p.id}
                  style={{
                    background: '#ffffff', borderRadius: 12,
                    padding: 24, display: 'flex', flexDirection: 'column',
                    boxShadow: '0 1px 4px rgba(25,28,30,0.07)',
                    border: '1px solid rgba(199,196,216,0.15)',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)'
                    ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(25,28,30,0.1)'
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'none'
                    ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(25,28,30,0.07)'
                  }}
                >
                  {/* Card Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        background: iconStyle.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <IconComp size={22} color={iconStyle.color} />
                      </div>
                      <div>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 4px', lineHeight: 1.2 }}>
                          {p.name}
                        </h3>
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: '#3525cd',
                          background: 'rgba(53,37,205,0.07)', padding: '2px 8px',
                          borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.08em',
                        }}>
                          Live
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteProject(p.id)}
                      style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#c7c4d8', borderRadius: 6, transition: 'color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#ba1a1a')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#c7c4d8')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Stats Row */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    background: '#f2f4f6', borderRadius: 10, padding: '12px 8px',
                    marginBottom: 20,
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: 9, color: '#777587', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, margin: '0 0 4px' }}>Total</p>
                      <p style={{ fontSize: 20, fontWeight: 800, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0 }}>{p.feedback_count}</p>
                    </div>
                    <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(199,196,216,0.3)', borderRight: '1px solid rgba(199,196,216,0.3)' }}>
                      <p style={{ fontSize: 9, color: '#777587', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, margin: '0 0 4px' }}>Open</p>
                      <p style={{ fontSize: 20, fontWeight: 800, color: '#3525cd', fontFamily: 'Manrope, sans-serif', margin: 0 }}>{p.open_count}</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: 9, color: '#777587', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, margin: '0 0 4px' }}>This Week</p>
                      <p style={{ fontSize: 20, fontWeight: 800, color: '#a44100', fontFamily: 'Manrope, sans-serif', margin: 0 }}>+{p.week_count}</p>
                    </div>
                  </div>

                  {/* API Key */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 9, color: '#777587', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>API Key</label>
                    <div
                      className="api-key-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eceef0', padding: '9px 12px', borderRadius: 8, position: 'relative' }}
                    >
                      <code style={{ flex: 1, fontSize: 11, fontFamily: 'monospace', color: '#464555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {maskKey(p.api_key)}
                      </code>
                      <div className="api-key-actions" style={{ display: 'flex', gap: 4, opacity: 0, transition: 'opacity 0.15s' }}>
                        <button
                          onClick={() => copyKey(p.api_key)}
                          style={{ padding: 5, background: '#ffffff', border: 'none', borderRadius: 4, cursor: 'pointer', color: copied === p.api_key ? '#059669' : '#464555', transition: 'color 0.15s', display: 'flex' }}
                          title="Copy key"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          onClick={() => rotateKey(p.id)}
                          style={{ padding: 5, background: '#ffffff', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#ba1a1a', transition: 'color 0.15s', display: 'flex' }}
                          title="Rotate key"
                        >
                          <RotateCcw size={12} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid rgba(199,196,216,0.2)', marginTop: 'auto' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Link
                        to={`/dashboard/form-builder?project=${p.id}`}
                        style={{
                          padding: '7px 10px', background: '#eceef0', borderRadius: 8,
                          color: '#464555', fontSize: 11, fontWeight: 500, textDecoration: 'none',
                          display: 'flex', alignItems: 'center', gap: 4,
                          transition: 'background 0.15s',
                        }}
                        title="Form Builder"
                      >
                        Form Builder
                      </Link>
                      <Link
                        to={`/dashboard/setup?project=${p.id}`}
                        style={{
                          padding: '7px 10px', background: '#eceef0', borderRadius: 8,
                          color: '#464555', fontSize: 11, fontWeight: 500, textDecoration: 'none',
                          display: 'flex', alignItems: 'center', gap: 4,
                          transition: 'background 0.15s',
                        }}
                        title="Install SDK"
                      >
                        Install SDK
                      </Link>
                    </div>
                  </div>

                  <Link
                    to={`/dashboard/feedback?project=${p.id}`}
                    style={{
                      display: 'block', width: '100%', marginTop: 10,
                      padding: '9px 0', textAlign: 'center',
                      background: 'rgba(53,37,205,0.07)', color: '#3525cd',
                      borderRadius: 8, fontSize: 12, fontWeight: 700,
                      textDecoration: 'none', transition: 'background 0.15s, color 0.15s',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLAnchorElement).style.background = '#3525cd'
                      ;(e.currentTarget as HTMLAnchorElement).style.color = '#ffffff'
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(53,37,205,0.07)'
                      ;(e.currentTarget as HTMLAnchorElement).style.color = '#3525cd'
                    }}
                  >
                    View Feedback
                  </Link>
                </div>
              )
            })}

            {/* Create New Project Card */}
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: 'rgba(242,244,246,0.6)', borderRadius: 12,
                border: '2px dashed rgba(199,196,216,0.6)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: 32, cursor: 'pointer', minHeight: 360,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = '#f2f4f6'
                ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#3525cd'
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(242,244,246,0.6)'
                ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(199,196,216,0.6)'
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#ffffff', boxShadow: '0 1px 4px rgba(25,28,30,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, transition: 'background 0.2s',
              }}>
                <Plus size={24} color="#3525cd" />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                Create New Project
              </h3>
              <p style={{ fontSize: 13, color: '#777587', margin: 0, textAlign: 'center', maxWidth: 180, lineHeight: 1.5 }}>
                Connect a new digital product to start receiving feedback immediately.
              </p>
            </button>
          </div>

          {/* Weekly Insight Banner */}
          {projects.length > 0 && (
            <div style={{
              marginTop: 40, padding: '28px 32px', borderRadius: 16,
              background: 'rgba(53,37,205,0.04)', border: '1px solid rgba(53,37,205,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(53,37,205,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <TrendingUp size={28} color="#3525cd" />
                </div>
                <div>
                  <h4 style={{ fontSize: 18, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 4px' }}>Weekly Insight</h4>
                  <p style={{ fontSize: 14, color: '#464555', margin: 0, lineHeight: 1.5 }}>
                    Your <strong style={{ color: '#191c1e' }}>"{projects[0]?.name}"</strong> project is actively collecting feedback. Keep your SDK up to date for the best experience.
                  </p>
                </div>
              </div>
              <Link
                to="/dashboard/feedback"
                style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', background: '#ffffff', borderRadius: 10,
                  boxShadow: '0 1px 4px rgba(25,28,30,0.1)', border: '1px solid #eceef0',
                  color: '#3525cd', fontWeight: 700, fontSize: 13, textDecoration: 'none',
                  transition: 'box-shadow 0.15s',
                }}
              >
                Deep Dive <TrendingUp size={15} />
              </Link>
            </div>
          )}
        </>
      )}

      {/* Create Project Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(25,28,30,0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{
            background: '#ffffff', borderRadius: 16,
            boxShadow: '0 24px 60px rgba(25,28,30,0.15)',
            padding: 32, width: 480, position: 'relative',
          }}>
            <button onClick={() => { setShowModal(false); setCreateError(null) }}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#777587', cursor: 'pointer', padding: 4, borderRadius: 6 }}>
              <X size={18} />
            </button>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, marginTop: 0, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>
              Create New Project
            </h2>
            <p style={{ fontSize: 14, color: '#777587', marginTop: 0, marginBottom: 24 }}>
              Each project gets its own API key and feedback inbox.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Input label="Project name" value={name} onChange={setName} placeholder="e.g. My App" />
              {createError && (
                <p style={{ margin: 0, fontSize: 13, color: '#ba1a1a', background: 'rgba(186,26,26,0.06)', padding: '10px 14px', borderRadius: 8 }}>
                  {createError}
                </p>
              )}
              <Button onClick={createProject} disabled={creating || !name.trim()} style={{ width: '100%', justifyContent: 'center' }}>
                {creating ? 'Creating…' : 'Create Project'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .api-key-row:hover .api-key-actions { opacity: 1 !important; }
      `}</style>

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          danger={confirmModal.danger}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  )
}
